(() => {
  const SearchElement = customElements.get('predictive-search');
  if (!SearchElement || SearchElement.prototype.__peakstyleEnhanced) return;

  const proto = SearchElement.prototype;
  proto.__peakstyleEnhanced = true;
  const originalParseQuery = proto.parseQuery;
  const originalApplyVariantCriteria = proto.applyVariantCriteria;
  const originalVariantMatches = proto.variantMatches;

  const fallbackDictionary = {
    terms: [
      { canonical: 'helm', aliases: ['fahrradhelm', 'fahrradhelme', 'bikehelm', 'bikehelme', 'radhelm', 'radhelme'] },
      { canonical: 'regenjacke', aliases: ['regenjacken', 'regenmantel', 'regenmaentel', 'regenmäntel', 'hardshell', 'hardshelljacke', 'rain jacket', 'rainjacket'] }
    ],
    sizes: {
      XXS: ['xxs', '2xs'], XS: ['xs'], S: ['s'], M: ['m'], L: ['l'], XL: ['xl'],
      XXL: ['xxl', '2xl'], '3XL': ['xxxl', '3xl'], '4XL': ['xxxxl', '4xl'], '5XL': ['5xl']
    },
    colors: {}
  };

  const semanticRules = {
    waterproof: {
      triggers: ['regenjacke', 'regenjacken', 'regenmantel', 'regenmaentel', 'regenmäntel', 'hardshell', 'hardshelljacke', 'wasserdicht', 'waterproof', 'rain jacket', 'rainjacket'],
      removeFromBackend: ['regenjacke', 'regenjacken', 'regenmantel', 'regenmaentel', 'regenmäntel', 'hardshell', 'hardshelljacke', 'wasserdicht', 'waterproof', 'rain jacket', 'rainjacket'],
      positive: [
        'wasserdicht', 'waterproof', 'gore-tex', 'gore tex', 'goretex', 'gore-tex pro', 'gore tex pro',
        'dermizax', 'futurelight', 'dryvent', 'hyvent', 'h2no', 'texapore', 'ceplex', 'sympatex',
        'event waterproof', 'e-vent waterproof', 'omni-tech waterproof', 'dryedge waterproof',
        'mp+ membrane', 'proof eco', 'helly tech protection', 'helly tech performance'
      ],
      negativeOnly: ['wasserabweisend', 'water repellent', 'water-repellent', 'water resistant', 'water-resistant']
    }
  };

  let dictionary = fallbackDictionary;
  let compiledTerms = [];
  let sizeAliases = new Map();
  const productCorpusCache = new Map();

  const normalizeStandalone = (value) => String(value || '')
    .toLocaleLowerCase('de-DE')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[®™]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const compileDictionary = (source) => {
    dictionary = source && typeof source === 'object' ? source : fallbackDictionary;
    compiledTerms = [];
    sizeAliases = new Map();

    (dictionary.terms || []).forEach((group) => {
      const canonical = normalizeStandalone(group.canonical);
      [group.canonical, ...(group.aliases || [])].forEach((alias) => {
        const normalizedAlias = normalizeStandalone(alias);
        if (canonical && normalizedAlias) compiledTerms.push({ alias: normalizedAlias, canonical });
      });
    });
    compiledTerms.sort((a, b) => b.alias.length - a.alias.length);

    Object.entries(dictionary.sizes || {}).forEach(([canonical, aliases]) => {
      [canonical, ...(aliases || [])].forEach((alias) => sizeAliases.set(normalizeStandalone(alias), canonical));
    });
  };

  compileDictionary(fallbackDictionary);

  const enhancementScriptUrl = document.currentScript?.src || [...document.scripts].map((script) => script.src).find((src) => src.includes('search-enhancements.js')) || '';
  const dictionaryUrl = window.PeakStyleSearchDictionaryUrl || enhancementScriptUrl.replace(/search-enhancements\.js(?:\?.*)?$/, 'search-dictionary.json');
  const dictionaryReady = dictionaryUrl
    ? fetch(dictionaryUrl, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Dictionary ${response.status}`);
        return response.json();
      })
      .then((data) => compileDictionary(data))
      .catch(() => compileDictionary(fallbackDictionary))
    : Promise.resolve();

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const containsPhrase = (text, phrase) => normalizeStandalone(text).includes(normalizeStandalone(phrase));

  const normalizeDictionaryTerms = (query) => {
    let result = ` ${normalizeStandalone(query)} `;
    compiledTerms.forEach(({ alias, canonical }) => {
      const pattern = new RegExp(`(^|\\s)${escapeRegExp(alias)}(?=\\s|$)`, 'g');
      result = result.replace(pattern, `$1${canonical}`);
    });
    return result.replace(/\s+/g, ' ').trim();
  };

  const detectRequirements = (query) => {
    const normalized = normalizeStandalone(query);
    return {
      waterproof: semanticRules.waterproof.triggers.some((term) => containsPhrase(normalized, term))
    };
  };

  const removeRequirementTerms = (query, requirements) => {
    let result = ` ${normalizeStandalone(query)} `;
    if (requirements.waterproof) {
      semanticRules.waterproof.removeFromBackend
        .map(normalizeStandalone)
        .sort((a, b) => b.length - a.length)
        .forEach((term) => {
          result = result.replace(new RegExp(`(^|\\s)${escapeRegExp(term)}(?=\\s|$)`, 'g'), '$1');
        });
    }
    return result.replace(/\s+/g, ' ').trim();
  };

  const detectAlphaSize = (query) => {
    const normalized = normalizeStandalone(query);
    const candidates = [...sizeAliases.keys()].sort((a, b) => b.length - a.length);
    for (const alias of candidates) {
      const explicit = new RegExp(`\\b(?:grosse|groesse|gr\\.?|size)\\s*[:\\-]?\\s*(${escapeRegExp(alias)})(?=\\s|$)`, 'i');
      const trailing = new RegExp(`(?:^|\\s)(${escapeRegExp(alias)})\\s*$`, 'i');
      const match = normalized.match(explicit) || normalized.match(trailing);
      if (match) return { alias: match[1], canonical: sizeAliases.get(normalizeStandalone(match[1])) };
    }
    return null;
  };

  const isAlphaSize = (value) => sizeAliases.has(normalizeStandalone(value)) || [...sizeAliases.values()].some((item) => normalizeStandalone(item) === normalizeStandalone(value));

  const mergeDictionaryColors = function() {
    Object.entries(dictionary.colors || {}).forEach(([canonical, aliases]) => {
      const key = this.normalizeText(canonical);
      const values = [canonical, ...(aliases || [])].map((value) => this.normalizeText(value));
      this.colorGroups[key] = [...new Set([...(this.colorGroups[key] || []), ...values])];
    });
  };

  proto.parseQuery = function(rawQuery) {
    mergeDictionaryColors.call(this);
    const normalized = this.normalizeText(rawQuery);
    const alphaSize = detectAlphaSize(normalized);
    let queryWithoutAlphaSize = normalized;

    if (alphaSize) {
      const aliasPattern = escapeRegExp(alphaSize.alias);
      queryWithoutAlphaSize = queryWithoutAlphaSize
        .replace(new RegExp(`\\b(?:grosse|groesse|gr\\.?|size)\\s*[:\\-]?\\s*${aliasPattern}(?=\\s|$)`, 'gi'), ' ')
        .replace(new RegExp(`(?:^|\\s)${aliasPattern}\\s*$`, 'i'), ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    const parsed = originalParseQuery.call(this, queryWithoutAlphaSize || rawQuery);
    if (alphaSize) {
      parsed.size = alphaSize.canonical;
      parsed.displaySize = alphaSize.canonical;
    }
    parsed.requirements = detectRequirements(rawQuery);
    parsed.backendQuery = normalizeDictionaryTerms(parsed.backendQuery || queryWithoutAlphaSize || rawQuery);
    parsed.backendQuery = removeRequirementTerms(parsed.backendQuery, parsed.requirements) || parsed.backendQuery;
    return parsed;
  };

  proto.variantMatches = function(title, parsed) {
    if (!parsed?.size || !isAlphaSize(parsed.size)) return originalVariantMatches.call(this, title, parsed);

    const parts = this.normalizeText(title).split('/').map((part) => part.trim());
    const color = parts[0] || '';
    const sizeText = parts.at(-1) || '';
    const requested = normalizeStandalone(parsed.size);
    const requestedCanonical = normalizeStandalone(sizeAliases.get(requested) || parsed.size);
    const sizeTokens = sizeText.split(/[^a-z0-9]+/).filter(Boolean);
    const matchingAliases = [...sizeAliases.entries()]
      .filter(([, canonical]) => normalizeStandalone(canonical) === requestedCanonical)
      .map(([alias]) => alias);

    if (!matchingAliases.some((alias) => sizeTokens.includes(alias))) return false;

    if (parsed.colorKey) {
      const aliases = [parsed.colorKey, ...(this.colorGroups[parsed.colorKey] || [])].map((value) => this.normalizeText(value));
      if (!aliases.some((value) => color.includes(value))) return false;
    }
    return true;
  };

  proto.getSearchResults = async function(rawQuery) {
    await dictionaryReady;
    const parsed = this.parseQuery(rawQuery);
    const key = this.normalizeText(rawQuery);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key], rawQuery, parsed);
      return;
    }

    this.abortPendingRequest();
    this.abortController = new AbortController();
    const requestId = ++this.requestSequence;
    const searchUrl = window.FoxThemeSettings?.routes?.search_url || '/search';
    const params = new URLSearchParams({
      q: parsed.backendQuery || rawQuery,
      type: 'product',
      section_id: 'quick-search-products',
      'options[prefix]': 'last',
      'options[unavailable_products]': 'hide'
    });

    fetch(`${searchUrl}?${params}`, { signal: this.abortController.signal, headers: { Accept: 'text/html' } })
      .then(async (response) => {
        const text = await response.text();
        if (!response.ok) throw new Error(`${response.status}: ${text}`);
        return text;
      })
      .then((text) => {
        if (requestId !== this.requestSequence || rawQuery !== this.getQuery()) return;
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-quick-search-products');
        if (!section) throw new Error('Quick-search section missing');
        const markup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => { instance.cachedResults[key] = markup; });
        this.renderSearchResults(markup, rawQuery, parsed);
      })
      .catch((error) => { if (error.name !== 'AbortError') this.renderErrorState(); })
      .finally(() => { if (requestId === this.requestSequence) this.abortController = null; });
  };

  const getProductPath = (item) => {
    const link = item?.querySelector?.('a[href*="/products/"]');
    if (!link) return '';
    try { return new URL(link.href, location.origin).pathname; } catch (_) { return ''; }
  };

  const textFromProductPage = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script:not([type="application/ld+json"]), style, noscript, header, footer, nav, [class*="recommend"], [id*="recommend"]').forEach((node) => node.remove());
    const preferred = doc.querySelector('main [id*="MainProduct"], main product-info, main .f-product, main .product, main') || doc.body;
    const meta = [...doc.querySelectorAll('meta[name="description"], meta[property="og:description"]')].map((node) => node.content || '').join(' ');
    const jsonLd = [...doc.querySelectorAll('script[type="application/ld+json"]')].map((node) => node.textContent || '').join(' ');
    return `${preferred?.textContent || ''} ${meta} ${jsonLd}`;
  };

  const loadProductCorpus = async (path) => {
    if (!path) return '';
    if (productCorpusCache.has(path)) return productCorpusCache.get(path);
    const promise = Promise.allSettled([
      fetch(`${path}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.ok ? response.json() : null),
      fetch(path, { headers: { Accept: 'text/html' } }).then((response) => response.ok ? response.text() : '')
    ]).then(([jsonResult, htmlResult]) => {
      const product = jsonResult.status === 'fulfilled' ? jsonResult.value : null;
      const html = htmlResult.status === 'fulfilled' ? htmlResult.value : '';
      const jsonCorpus = product ? [product.title, product.vendor, product.type, product.tags, product.description, ...(product.variants || []).map((variant) => variant.title)].flat().join(' ') : '';
      return normalizeStandalone(`${jsonCorpus} ${textFromProductPage(html)}`);
    }).catch(() => '');
    productCorpusCache.set(path, promise);
    return promise;
  };

  const isWaterproof = (corpus) => {
    const normalized = normalizeStandalone(corpus);
    if (!normalized) return false;
    const positives = semanticRules.waterproof.positive.some((term) => containsPhrase(normalized, term));
    if (positives) return true;
    const onlyWeakWaterProtection = semanticRules.waterproof.negativeOnly.some((term) => containsPhrase(normalized, term));
    return !onlyWeakWaterProtection && /\bwaterproof\b|\bwasserdicht\b/.test(normalized);
  };

  const matchesSemanticRequirements = (corpus, requirements) => {
    if (requirements?.waterproof && !isWaterproof(corpus)) return false;
    return true;
  };

  const semanticLabel = (requirements) => requirements?.waterproof ? 'wasserdichte Regenjacken' : 'passende Produkte';

  proto.applySemanticCriteria = async function(parsed) {
    if (!parsed?.requirements || !Object.values(parsed.requirements).some(Boolean)) return;
    const items = [...this.querySelectorAll('[data-available-variants]')];
    if (!items.length) return;

    const states = await Promise.all(items.map(async (item) => {
      const corpus = await loadProductCorpus(getProductPath(item));
      return { item, matches: matchesSemanticRequirements(corpus, parsed.requirements) };
    }));

    let visible = 0;
    states.forEach(({ item, matches }) => {
      if (!matches) {
        item.hidden = true;
        item.dataset.semanticHidden = 'true';
      } else if (!item.dataset.searchLimitHidden) {
        visible += 1;
      }
    });

    this.querySelector('.search-semantic-notice')?.remove();
    const notice = document.createElement('div');
    notice.className = `search-semantic-notice${visible ? '' : ' search-semantic-notice--empty'}`;
    notice.innerHTML = visible
      ? `<strong>Fachlich gefiltert:</strong><span>Es werden nur ${semanticLabel(parsed.requirements)} angezeigt. „Wasserabweisend“ genügt nicht.</span>`
      : `<strong>Keine fachlich passenden Treffer.</strong><span>Für „${semanticLabel(parsed.requirements)}“ werden nur Produkte mit bestätigter wasserdichter Eigenschaft oder anerkannter wasserdichter Membran angezeigt.</span>`;
    this.predictiveSearchResults.prepend(notice);

    const relaxed = this.querySelector('.search-predictive-relaxed');
    if (relaxed) relaxed.hidden = true;
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) count.textContent = `${visible} fachlich passende Treffer.`;
  };

  proto.applyResultLimit = function() {
    const maximum = window.matchMedia('(min-width: 768px)').matches ? 12 : 6;
    const items = [...this.querySelectorAll('[data-available-variants]')].filter((item) => !item.hidden);
    items.forEach((item, index) => {
      if (index >= maximum) {
        item.hidden = true;
        item.dataset.searchLimitHidden = 'true';
      } else {
        delete item.dataset.searchLimitHidden;
      }
    });
  };

  proto.applyVariantCriteria = async function(parsed) {
    await originalApplyVariantCriteria.call(this, parsed);
    await this.applySemanticCriteria(parsed);
    this.applyResultLimit();
  };

  const patchMainSearch = async () => {
    await customElements.whenDefined('main-search');
    const MainSearch = customElements.get('main-search');
    if (!MainSearch || MainSearch.prototype.__peakstyleSemanticEnhanced) return;
    MainSearch.prototype.__peakstyleSemanticEnhanced = true;
    const mainParse = MainSearch.prototype.parseQuery;
    MainSearch.prototype.parseQuery = function(query) {
      const parsed = mainParse.call(this, query);
      parsed.requirements = detectRequirements(query);
      parsed.baseQuery = removeRequirementTerms(parsed.baseQuery || query, parsed.requirements) || parsed.baseQuery;
      return parsed;
    };
  };

  const filterMainSearchResults = async () => {
    if (!document.querySelector('main-search')) return;
    const params = new URLSearchParams(location.search);
    const original = params.get('ps_original') || params.get('q') || '';
    const requirements = detectRequirements(original);
    if (!Object.values(requirements).some(Boolean)) return;

    const grid = document.querySelector('[data-products-grid]');
    if (!grid) return;
    const columns = [...grid.querySelectorAll(':scope > .f-column')].filter((column) => column.querySelector('a[href*="/products/"]'));
    if (!columns.length) return;

    const states = await Promise.all(columns.map(async (column) => {
      const corpus = await loadProductCorpus(getProductPath(column));
      return { column, matches: matchesSemanticRequirements(corpus, requirements) };
    }));
    let visible = 0;
    states.forEach(({ column, matches }) => {
      column.hidden = !matches;
      if (matches) visible += 1;
    });

    document.querySelector('.search-semantic-page-notice')?.remove();
    const notice = document.createElement('div');
    notice.className = `search-semantic-page-notice${visible ? '' : ' search-semantic-page-notice--empty'}`;
    notice.innerHTML = visible
      ? `<strong>${visible} fachlich passende Produkte</strong><span>Nur ${semanticLabel(requirements)}; wasserabweisende Produkte wurden ausgeschlossen.</span>`
      : `<strong>Keine bestätigten wasserdichten Regenjacken gefunden.</strong><span>Es werden keine lediglich wasserabweisenden oder fachlich unklaren Produkte als Regenjacke ausgegeben.</span>`;
    grid.before(notice);

    const count = document.querySelector('[data-product-count]');
    if (count) count.textContent = `${visible} fachlich passende${visible === 1 ? 's' : ''} Produkt${visible === 1 ? '' : 'e'}`;
  };

  const injectStyles = () => {
    if (document.getElementById('peakstyle-semantic-search-styles')) return;
    const style = document.createElement('style');
    style.id = 'peakstyle-semantic-search-styles';
    style.textContent = `
      #Drawer-Search{--f-drawer-width:min(1320px,96vw)!important;--f-search-columns:3}
      #Drawer-Search .f-predictive-search__block-products .f-predictive-search__results-list{gap:3rem 2.4rem!important}
      #Drawer-Search .f-predictive-search__block-products li[role="option"]{min-width:0}
      #Drawer-Search .f-predictive-search__block-products .f-predictive-search__item{display:block!important;height:100%;padding:0!important}
      #Drawer-Search .f-predictive-search__block-products .f-predictive-search__item-image{width:100%!important;aspect-ratio:1/1!important;margin:0 0 1.4rem!important;overflow:hidden;background:rgba(var(--color-foreground),.025)}
      #Drawer-Search .f-predictive-search__block-products .f-predictive-search__item-image img{width:100%!important;height:100%!important;object-fit:contain!important;display:block}
      #Drawer-Search .f-predictive-search__item-content{padding:0 .2rem 1rem}
      #Drawer-Search .f-predictive-search__item-vendor{font-size:1.3rem;margin-bottom:.45rem;text-transform:none}
      #Drawer-Search .f-predictive-search__item-heading{font-size:clamp(1.5rem,1.15vw,1.8rem);line-height:1.3;min-height:2.6em;margin-bottom:.8rem;-webkit-line-clamp:2!important}
      #Drawer-Search .f-predictive-search__item-meta{display:flex;align-items:baseline;flex-wrap:wrap;column-gap:.8rem;row-gap:.35rem;font-size:1.55rem}
      #Drawer-Search .f-predictive-search__current-price{font-weight:700}
      #Drawer-Search .f-predictive-search__compare-price{opacity:.58}
      #Drawer-Search .f-predictive-search__discount{display:block;width:100%;font-weight:650;margin-top:.2rem}
      #Drawer-Search .f-predictive-search__availability,#Drawer-Search .f-predictive-search__match-note{display:block;margin-top:.45rem;font-size:1.35rem}
      .search-semantic-notice,.search-semantic-page-notice{display:flex;gap:.7rem 1.2rem;align-items:baseline;flex-wrap:wrap;padding:1.2rem 1.5rem;margin:0 0 2rem;background:rgba(var(--color-foreground),.045);border-left:3px solid currentColor}
      .search-semantic-notice--empty,.search-semantic-page-notice--empty{padding:2rem}
      .search-semantic-page-notice{margin:0 0 2.4rem}
      @media(max-width:1199.98px){#Drawer-Search{--f-drawer-width:min(980px,94vw)!important;--f-search-columns:2}}
      @media(max-width:767.98px){#Drawer-Search{--f-drawer-width:100vw!important;--f-search-columns:2}#Drawer-Search .f-predictive-search__block-products .f-predictive-search__results-list{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:2.2rem 1.2rem!important}#Drawer-Search .f-predictive-search__item-heading{font-size:1.35rem}#Drawer-Search .f-predictive-search__item-meta{font-size:1.35rem}#Drawer-Search .f-predictive-search__availability,#Drawer-Search .f-predictive-search__match-note{font-size:1.2rem}}
    `;
    document.head.appendChild(style);
  };

  injectStyles();
  patchMainSearch();
  window.addEventListener('load', () => setTimeout(filterMainSearchResults, 900), { once: true });
})();
